class HeadphoneModelFinder extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('[data-model-finder-input]');
    this.results = this.querySelector('[data-model-finder-results]');
    this.status = this.querySelector('[data-model-finder-status]');
    this.searchLink = this.querySelector('[data-model-finder-search-link]');
    this.debounceTimer = null;
    this.productIndex = null;
    this.productIndexPromise = null;
  }

  connectedCallback() {
    if (!this.input || !this.results || !this.status) return;
    this.input.addEventListener('input', () => {
      window.clearTimeout(this.debounceTimer);
      this.debounceTimer = window.setTimeout(() => this.search(), 250);
    });
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
  }

  async search() {
    const term = this.input.value.trim();
    this.updateSearchLink(term);
    if (term.length < 2) {
      this.clearResults();
      return;
    }
    this.setLoading(true);
    try {
      const index = await this.loadProductIndex();
      const query = this.normalize(term);
      const compactQuery = this.compact(term);
      if (compactQuery.length < 2) {
        this.clearResults();
        return;
      }
      const products = index
        .map((product) => {
          let score = 0;
          if (product.compactTitle.includes(compactQuery)) score += 30;
          if (product.compactHandle.includes(compactQuery)) score += 25;
          if (product.compactDescription.includes(compactQuery)) score += 20;
          if (product.normalizedDescription.includes(query)) score += 5;
          return { ...product, score };
        })
        .filter((product) => product.score > 0)
        .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title))
        .slice(0, 8);
      this.renderResults(products, term);
    } catch (error) {
      this.renderError();
    } finally {
      this.setLoading(false);
    }
  }

  async loadProductIndex() {
    if (this.productIndex) return this.productIndex;
    if (this.productIndexPromise) return this.productIndexPromise;

    this.productIndexPromise = fetch(this.dataset.productsUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`Product index request failed: ${response.status}`);
        return response.json();
      })
      .then(({ products }) => {
        this.productIndex = products.map((product) => {
          const parsedDescription = new DOMParser().parseFromString(product.body_html || '', 'text/html');
          const description = parsedDescription.body.textContent.replace(/\s+/g, ' ').trim();
          const image = product.images?.[0];
          return {
            title: product.title,
            url: `/products/${product.handle}`,
            imageUrl: image?.src || '',
            imageAlt: image?.alt || product.title,
            normalizedDescription: this.normalize(description),
            compactDescription: this.compact(description),
            compactTitle: this.compact(product.title),
            compactHandle: this.compact(product.handle),
          };
        });
        return this.productIndex;
      })
      .catch((error) => {
        this.productIndexPromise = null;
        throw error;
      });

    return this.productIndexPromise;
  }

  normalize(value) {
    return value
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  compact(value) {
    return this.normalize(value).replace(/\s+/g, '');
  }

  renderResults(products, term) {
    this.results.replaceChildren();
    if (!products.length) {
      this.status.textContent = this.dataset.noResults.replace('[term]', term);
      this.results.hidden = false;
      this.searchLink.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      return;
    }

    const list = document.createElement('ul');
    list.className = 'model-finder__list list-unstyled';
    list.setAttribute('role', 'listbox');
    products.forEach((product, index) => {
      const item = document.createElement('li');
      item.setAttribute('role', 'option');
      const link = document.createElement('a');
      link.className = 'model-finder__result link link--text';
      link.href = product.url;
      link.dataset.modelFinderOption = '';
      if (index === 0) link.dataset.firstOption = '';

      if (product.imageUrl) {
        const image = document.createElement('img');
        image.className = 'model-finder__image';
        image.src = product.imageUrl;
        image.alt = product.imageAlt;
        image.width = 72;
        image.height = 72;
        image.loading = 'lazy';
        link.append(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'model-finder__image model-finder__image--placeholder';
        placeholder.setAttribute('aria-hidden', 'true');
        link.append(placeholder);
      }

      const copy = document.createElement('span');
      copy.className = 'model-finder__result-copy';
      const eyebrow = document.createElement('span');
      eyebrow.className = 'caption-with-letter-spacing';
      eyebrow.textContent = product.compactTitle.includes(this.compact(term))
        ? this.dataset.nameMatchLabel
        : this.dataset.compatibilityMatchLabel;
      const title = document.createElement('span');
      title.className = 'model-finder__result-title h5';
      title.textContent = product.title;
      copy.append(eyebrow, title);
      link.append(copy);
      item.append(link);
      list.append(item);
    });

    this.results.append(list);
    this.status.textContent = this.dataset.resultCount.replace('[count]', products.length).replace('[term]', term);
    this.results.hidden = false;
    this.searchLink.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  renderError() {
    this.results.replaceChildren();
    this.results.hidden = false;
    this.searchLink.hidden = false;
    this.status.textContent = this.dataset.errorMessage;
    this.input.setAttribute('aria-expanded', 'true');
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.clearResults();
      this.input.focus();
    } else if (event.key === 'ArrowDown') {
      const firstOption = this.querySelector('[data-first-option]');
      if (firstOption) {
        event.preventDefault();
        firstOption.focus();
      }
    }
  }

  updateSearchLink(term) {
    if (!this.searchLink) return;
    const url = new URL(this.searchLink.dataset.baseUrl, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('type', 'product');
    this.searchLink.href = `${url.pathname}${url.search}`;
  }

  setLoading(isLoading) {
    this.toggleAttribute('loading', isLoading);
    this.input.setAttribute('aria-busy', String(isLoading));
    if (isLoading) this.status.textContent = this.dataset.loadingMessage;
  }

  clearResults() {
    this.results.replaceChildren();
    this.results.hidden = true;
    this.searchLink.hidden = true;
    this.status.textContent = '';
    this.input.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('headphone-model-finder')) {
  customElements.define('headphone-model-finder', HeadphoneModelFinder);
}
